import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';

const PIPELINE_STATUS_COLORS = {
    published: 'success',
    evaluator_passed: 'success',
    failed: 'error',
    evaluator_rejected: 'error',
    duplicate_rejected: 'error',
    queued: 'default',
    processing: 'warning',
    writing: 'warning',
    reviewing: 'warning',
    date_validating: 'warning',
    evaluation_skipped: 'default',
};

function formatDateTime(value, locale) {
    if (!value) return '–';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '–';
    return date.toLocaleString(locale ?? 'fi-FI', {
        dateStyle: 'short',
        timeStyle: 'short',
    });
}

function humanize(value) {
    if (!value) return '–';
    return String(value).replace(/_/g, ' ');
}

function formatPipelineEventLabel(event, intl) {
    const base = intl.formatMessage({
        id: `monitoring.run.event.${event.event_type}`,
        defaultMessage: humanize(event.event_type),
    });
    if (event.event_type !== 'duplicate_check_completed') {
        return base;
    }
    const payload = event.payload ?? {};
    const suffix = [payload.mode, payload.reason].filter(Boolean).join(' · ');
    return suffix ? `${base} (${suffix})` : base;
}

function latestDuplicateCheckPayload(events) {
    if (!events?.length) return null;
    for (let index = events.length - 1; index >= 0; index -= 1) {
        if (events[index].event_type === 'duplicate_check_completed') {
            return events[index].payload ?? null;
        }
    }
    return null;
}

function formatConfidence(intl, confidence) {
    if (confidence == null || Number.isNaN(Number(confidence))) return null;
    return intl.formatNumber(Number(confidence), {
        style: 'percent',
        maximumFractionDigits: 0,
    });
}

/** Evaluator events store `model`; writer/review store `writer_deployment`. */
export function eventLlmLabel(event) {
    const payload = event?.payload;
    if (!payload || typeof payload !== 'object') {
        return '–';
    }
    const value = payload.model ?? payload.writer_deployment;
    if (value == null || String(value).trim() === '') {
        return '–';
    }
    return String(value);
}

function PipelineStatusChip({ status }) {
    const intl = useIntl();
    const color = PIPELINE_STATUS_COLORS[status] ?? 'default';
    const label = intl.formatMessage(
        { id: `monitoring.run.pipelineStatus.${status}`, defaultMessage: humanize(status) }
    );

    return <Chip size="small" color={color} variant="outlined" label={label} />;
}

function PipelineItemSection({ detail }) {
    const intl = useIntl();
    const { item, events } = detail;
    const title = item.content_title || item.content_key || item.id;
    const [showRejectionReason, setShowRejectionReason] = useState(false);
    const duplicateCheckPayload = latestDuplicateCheckPayload(events);
    const rejectionExplanation =
        item.evaluator_explanation ||
        (duplicateCheckPayload?.reason ? humanize(duplicateCheckPayload.reason) : null);
    const rejectionConfidence = formatConfidence(intl, duplicateCheckPayload?.confidence);
    const hasRejectionReason =
        (item.status === 'evaluator_rejected' || item.status === 'duplicate_rejected') &&
        Boolean(rejectionExplanation || rejectionConfidence);

    return (
        <Box sx={{ mb: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 1,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {item.target_media ? (
                        <>
                            <Box
                                component="span"
                                sx={{ color: 'primary.main', fontWeight: 600, mr: 0.5 }}
                            >
                                {item.target_media}
                            </Box>
                            <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                                ·
                            </Box>
                            {title}
                        </>
                    ) : (
                        title
                    )}
                </Typography>
                <PipelineStatusChip status={item.status} />
                {hasRejectionReason && (
                    <Button
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() => setShowRejectionReason((open) => !open)}
                        sx={{ minWidth: 0, px: 0.5, textTransform: 'none' }}
                    >
                        {intl.formatMessage({
                            id: showRejectionReason
                                ? 'monitoring.run.hideRejectionReason'
                                : 'monitoring.run.showRejectionReason',
                        })}
                    </Button>
                )}
                {item.content_url && (
                    <Link href={item.content_url} target="_blank" rel="noopener noreferrer" variant="body2">
                        {intl.formatMessage({ id: 'monitoring.run.viewContent' })}
                    </Link>
                )}
            </Box>
            {hasRejectionReason && showRejectionReason && (
                <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                    {rejectionExplanation}
                    {rejectionConfidence && (
                        <>
                            {rejectionExplanation ? ' · ' : ''}
                            {intl.formatMessage(
                                { id: 'monitoring.run.duplicateConfidence' },
                                { confidence: rejectionConfidence }
                            )}
                        </>
                    )}
                </Typography>
            )}
            {item.error_text && (
                <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                    {item.error_text}
                </Typography>
            )}
            {events.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: 'monitoring.run.noEvents' })}
                </Typography>
            ) : (
                <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                {intl.formatMessage({ id: 'monitoring.run.column.occurredAt' })}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                {intl.formatMessage({ id: 'monitoring.run.column.event' })}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                {intl.formatMessage({ id: 'monitoring.run.column.stage' })}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                {intl.formatMessage({ id: 'monitoring.run.column.llm' })}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                {intl.formatMessage({ id: 'monitoring.run.column.statusAfter' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {events.map((event) => {
                            const llmLabel = eventLlmLabel(event);
                            return (
                                <TableRow key={event.id} hover>
                                    <TableCell sx={{ borderColor: 'divider' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDateTime(event.occurred_at, intl.locale)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderColor: 'divider' }}>
                                        <Typography variant="body2">
                                            {formatPipelineEventLabel(event, intl)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderColor: 'divider' }}>
                                        <Typography variant="body2">{event.stage ?? '–'}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderColor: 'divider' }}>
                                        <Typography variant="body2">{llmLabel}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderColor: 'divider' }}>
                                        <Typography variant="body2">
                                            {intl.formatMessage({
                                                id: `monitoring.run.pipelineStatus.${event.status_after}`,
                                                defaultMessage: humanize(event.status_after),
                                            })}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}

export default function RunPipelineItemsList({ items }) {
    const intl = useIntl();

    if (!items?.length) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                {intl.formatMessage({ id: 'monitoring.run.empty' })}
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {items.map((detail) => (
                <PipelineItemSection key={detail.item.id} detail={detail} />
            ))}
        </Box>
    );
}
